// hooks/user/useCheckUsernameAvailability.js
import { useMutation } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Custom hook for checking username availability
 *
 * Implements retry logic for network failures and comprehensive error handling.
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.6
 *
 * @returns {Object} Mutation object with checkAvailability function
 */
export const useCheckUsernameAvailability = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = process.env.REACT_APP_API_URL;

  return useMutation(
    async (username) => {
      const token = await getAccessTokenSilently();

      const response = await axios.get(
        `${apiUrl}/api/users/check-username/${encodeURIComponent(username)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000, // 10 second timeout
        }
      );

      return response.data;
    },
    {
      retry: 2, // Retry failed requests up to 2 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000), // Exponential backoff: 1s, 2s
      onError: (error) => {
        // Handle errors silently for availability checks
        // The component will handle displaying appropriate messages
        if (error.code === "ECONNABORTED") {
          console.error("Username availability check timed out:", error);
        } else if (error.response) {
          // Server responded with error status
          console.error(
            "Server error checking username availability:",
            error.response.status,
            error.response.data
          );
        } else if (error.request) {
          // Request made but no response received
          console.error(
            "Network error checking username availability:",
            error.message
          );
        } else {
          // Something else happened
          console.error("Error checking username availability:", error.message);
        }
      },
    }
  );
};
